import { Button, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus, FaGear } from "react-icons/fa6";
import { api } from "@/api";
import {
  AvailableLockSystem,
  LockSystem,
} from "@/api/lockSystems/lockSystems.ts";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { PromptLockSystemModal } from "@/app/components/modals/lockSystems/promptLockSystem";
import { CreateTtlockSystemModal } from "@/app/components/modals/lockSystems/ttlock/createTtlockSystem";
import { EditTtlockSystemModal } from "@/app/components/modals/lockSystems/ttlock/editTtlockSystem";
import { InputField, ModalField } from "@/app/components/modals/modalBuilder";
import { CreateCustomSystemModal } from "@/app/components/modals/lockSystems/custom/createCustomSystem";
import { EditCustomSystemModal } from "@/app/components/modals/lockSystems/custom/editCustomSystem";
import {
  businessProfileLinks,
  Links,
} from "@/app/components/navigation/links/Links";
import { InputType } from "@/app/components/modals/builderTypes";
import { Link } from "react-router-dom";

export function convertType(type: string): InputType {
  switch (type) {
    case "string":
      return "text";
    case "password":
      return "password";
    default:
      return "text";
  }
}

export function convertFieldsToModalFields(
  fields: AvailableLockSystem["fields"],
): InputField[] {
  return fields.map((field) => ({
    name: field.field_name,
    label: field.field_label,
    type: convertType(field.field_type),
    placeholder: "",
    isRequired: field.required,
  }));
}

export function LockSystemsPage() {
  const [availableLockSystems, setAvailableLockSystems] = useState<
    AvailableLockSystem[]
  >([]);
  const [customTypes, setCustomTypes] = useState<string>("");

  const [lockSystems, setLockSystems] = useState<LockSystem[] | null>(null);
  const [lockSystem, setLockSystem] = useState<LockSystem | null>(null);

  const [fields, setFields] = useState<ModalField[]>([]);

  const handleError = useErrorHandling();

  const promptLockSystemModalDisclosure = useDisclosure();

  const createTtlockSystemModalDisclosure = useDisclosure();
  const editTtlockSystemModalDisclosure = useDisclosure();

  const createCustomSystemModalDisclosure = useDisclosure();
  const editCustomSystemModalDisclosure = useDisclosure();

  const selectLockSystem = useCallback(
    (lockSystem: AvailableLockSystem) => {
      promptLockSystemModalDisclosure.onClose();

      setFields(
        convertFieldsToModalFields(lockSystem.fields).map((field) => ({
          ...field,
          isRequired: true,
        })),
      );
      setCustomTypes(lockSystem.system);

      createCustomSystemModalDisclosure.onOpen();
    },
    [createCustomSystemModalDisclosure, promptLockSystemModalDisclosure],
  );

  const editLockSystem = useCallback(
    (lockSystem: LockSystem) => {
      console.log(lockSystem);
      setLockSystem(lockSystem);
      const availableSystem = availableLockSystems.find(
        (el) => el.system.toLowerCase() == lockSystem.type.toLowerCase(),
      );

      if (availableSystem) {
        setFields(convertFieldsToModalFields(availableSystem.fields));
        setCustomTypes(availableSystem.system);
        editCustomSystemModalDisclosure.onOpen();
      }
    },
    [availableLockSystems, editCustomSystemModalDisclosure],
  );

  useEffect(() => {
    api.lockSystems
      .getAvailableLockSystems()
      .then((response) => {
        setAvailableLockSystems(response.data.schemas || []);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.lockSystems
      .list()
      .then((response) => {
        setLockSystems(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    createTtlockSystemModalDisclosure.isOpen,
    editTtlockSystemModalDisclosure.isOpen,
    createCustomSystemModalDisclosure.isOpen,
    editCustomSystemModalDisclosure.isOpen,
    handleError,
  ]);

  return (
    <>
      <PromptLockSystemModal
        disclosure={promptLockSystemModalDisclosure}
        lockSystems={availableLockSystems}
        selectLockSystem={selectLockSystem}
      />

      <CreateTtlockSystemModal disclosure={createTtlockSystemModalDisclosure} />

      <CreateCustomSystemModal
        disclosure={createCustomSystemModalDisclosure}
        type={customTypes}
        fields={fields}
      />

      <EditTtlockSystemModal
        disclosure={editTtlockSystemModalDisclosure}
        lockSystem={lockSystem}
      />

      <EditCustomSystemModal
        disclosure={editCustomSystemModalDisclosure}
        type={customTypes}
        fields={fields}
        lockSystem={lockSystem}
      />

      <div className="flex flex-row gap-4 w-full">
        <Links links={businessProfileLinks("/dashboard/service/locks")} />
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Системы замков</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                size="sm"
                variant="solid"
                className="max-w-fit"
                onPress={() => promptLockSystemModalDisclosure.onOpen()}>
                Создать
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              columns={[
                {
                  label: "Название системы",
                  key: "display_name",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  label: "Тип",
                  key: "type",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Custom,
                  render: (_value: any, row: LockSystem) => (
                    <div className="flex flex-row gap-2 items-center">
                      <Button
                        size="sm"
                        variant="flat"
                        onClick={() => editLockSystem(row)}>
                        <FaPencil />
                      </Button>
                      {row.type.toLowerCase() === "sigur" && (
                        <Button
                          size="sm"
                          variant="flat"
                          as={Link}
                          to="/dashboard/service/sigur">
                          <FaGear />
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              data={lockSystems || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "display_name",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
